import { Group, ScrollArea, Stack } from "@mantine/core"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import {
  type OperationNodeDto,
  useFindOperationTreeByProductId,
} from "~/gen/api"
import { HorizontalDivider } from "~/pages/Brand/Products/Details/CustomFieldGraph/Divider"
import { VerticalDivider } from "~/pages/Brand/Products/Details/TraceabilityGraph/Divider"
import { TraceabilityTreeBranch } from "~/pages/Brand/Products/Details/TraceabilityGraph/TraceabilityTreeBranch"

type Props = {
  product: LibraryResponses.ProductDetails
}

export const ProductTraceability = ({ product }: Props) => {
  const { data: operationTreeResponse } = useFindOperationTreeByProductId(
    product.uuid
  )

  const operationTree = operationTreeResponse?.data
  const mainNodes = operationTree?.nodes.filter((node) => node.isMain)[0]

  if (!mainNodes) {
    return null
  }

  const map = new Map(operationTree?.nodes.map((node) => [node.id, node]))

  const result: OperationNodeDto[] = []
  const visited = new Set()

  function visit(item: OperationNodeDto) {
    if (!item || visited.has(item.id)) return

    visited.add(item.id)
    result.push(item)

    item.childrenRef.forEach((childId) => {
      visit(map.get(childId) as OperationNodeDto)
    })
  }

  const mainItem = operationTree?.nodes.find((node) => node.isMain)
  if (!mainItem) {
    return null
  }
  visit(mainItem)

  return (
    <ScrollArea w="100%" type="scroll" offsetScrollbars>
      {RenderTree(mainItem, map, false)}
    </ScrollArea>
  )
}

const RenderTree = (
  item: OperationNodeDto,
  map: Map<string, OperationNodeDto>,
  isFirstChild = true
) => {
  if (!item) return null

  return (
    <Group
      gap={0}
      align="flex-start"
      style={{ position: "relative" }}
      wrap="nowrap"
    >
      {!isFirstChild && <HorizontalDivider />}
      <TraceabilityTreeBranch node={item} hideFirstDivider={!isFirstChild} />
      {item.childrenRef.length > 0 && (
        <Stack align="flex-start" justify="left">
          <VerticalDivider
            itemCount={getVerticalDividerCoveredCardsCount(item, map)}
          />
          {item.childrenRef.map((childId) => (
            <Group key={childId} gap={0} align="flex-start" wrap="nowrap">
              {!childId.includes(item.type) && <HorizontalDivider />}
              {RenderTree(
                map.get(childId) as OperationNodeDto,
                map,
                !childId.includes(item.type)
              )}
            </Group>
          ))}
        </Stack>
      )}
    </Group>
  )
}

const getVerticalDividerCoveredCardsCount = (
  item: OperationNodeDto,
  map: Map<string, OperationNodeDto>
): number => {
  if (item.childrenRef.length <= 1) {
    return item.childrenRef.length
  }

  const siblingBranchRowsCount = item.childrenRef.map((childId) => {
    const childNode = map.get(childId)
    if (!childNode) {
      return 1
    }

    return getRenderedRowsCount(childNode, map)
  })

  const coveredRowsBeforeLastSibling = siblingBranchRowsCount
    .slice(0, -1)
    .reduce((sum, rowsCount) => sum + rowsCount, 0)

  return coveredRowsBeforeLastSibling + 1
}

const getRenderedRowsCount = (
  item: OperationNodeDto,
  map: Map<string, OperationNodeDto>
): number => {
  if (item.childrenRef.length === 0) {
    return 1
  }

  return item.childrenRef.reduce((sum, childId) => {
    const childNode = map.get(childId)
    if (!childNode) {
      return sum + 1
    }

    return sum + getRenderedRowsCount(childNode, map)
  }, 0)
}
